import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  UserCircleIcon,
  EyeIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  CalendarIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  BookOpenIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  PencilSquareIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@gradebook/ui";
import { useAuthStore, type UserRole } from "@/store/authStore";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { useSchoolRoutes } from "@/lib/routes";
import { ImpersonationUserPicker } from "@/components/auth/ImpersonationUserPicker";
import { BuildInfo } from "@/components/layout/BuildInfo";

const impersonationRoles: { value: UserRole; label: string }[] = [
  { value: "teacher", label: "Teacher" },
  { value: "student", label: "Student" },
  { value: "parent", label: "Parent" },
];

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

export function AppSidebar() {
  const { user, impersonation, startImpersonation, getEffectiveRole, logout } =
    useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useSchoolRoutes();
  const [impersonating, setImpersonating] = useState(false);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const effectiveRole = getEffectiveRole();
  const isAdmin = user?.role === "admin";
  const isImpersonating = impersonation?.isImpersonating ?? false;

  const isActive = (path: string) => {
    // Handle paths with school slug prefix
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setUserPickerOpen(true);
  };

  const handleUserSelect = async (user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  }) => {
    if (!selectedRole) return;

    setImpersonating(true);
    try {
      const response = await api.post("/api/auth/impersonate", {
        role: selectedRole,
        userId: user.id,
      });
      startImpersonation(
        response.data.token,
        response.data.impersonatingRole,
        response.data.impersonatedUser
      );
      setUserPickerOpen(false);
      setSelectedRole(null);
      navigate(routes.dashboard());
    } catch (error) {
      console.error("Failed to impersonate:", error);
    } finally {
      setImpersonating(false);
    }
  };

  const handleUserPickerClose = () => {
    setUserPickerOpen(false);
    setSelectedRole(null);
  };

  // Build navigation sections based on role
  const getNavSections = (): NavSection[] => {
    const sections: NavSection[] = [];

    // Main navigation - always visible
    const mainItems: NavItem[] = [
      { name: "Dashboard", href: routes.dashboard(), icon: HomeIcon },
      { name: "Calendar", href: routes.calendar(), icon: CalendarIcon },
    ];

    if (effectiveRole === "student") {
      mainItems.push({ name: "My Grades", href: routes.myGrades(), icon: AcademicCapIcon });
      mainItems.push({ name: "Year-End Grades", href: routes.myYearEndGrades(), icon: ClipboardDocumentCheckIcon });
    }

    if (effectiveRole === "parent") {
      mainItems.push({ name: "My Children", href: routes.parent(), icon: UserGroupIcon });
      mainItems.push({ name: "Year-End Grades", href: routes.myYearEndGrades(), icon: ClipboardDocumentCheckIcon });
    }

    if (effectiveRole === "teacher" || effectiveRole === "admin") {
      mainItems.push({ name: "Gradebook", href: routes.gradebook(), icon: BookOpenIcon });
      mainItems.push({ name: "Courses", href: routes.courses(), icon: AcademicCapIcon });
      mainItems.push({ name: "Import", href: routes.bulkImport(), icon: ArrowUpTrayIcon });
    }

    if (effectiveRole === "teacher") {
      mainItems.push({ name: "My Submissions", href: routes.mySubmissions(), icon: DocumentTextIcon });
      mainItems.push({ name: "My Grade Changes", href: routes.myGradeChanges(), icon: PencilSquareIcon });
    }

    sections.push({ items: mainItems });

    // People section for teachers/admins
    if (effectiveRole === "teacher" || effectiveRole === "admin") {
      const peopleItems: NavItem[] = [
        { name: "Students", href: routes.students(), icon: UserGroupIcon },
      ];

      if (isAdmin && !isImpersonating) {
        peopleItems.push({ name: "Teachers", href: routes.adminTeachers(), icon: UserGroupIcon });
        peopleItems.push({ name: "Parents", href: routes.adminParents(), icon: UserGroupIcon });
      }

      sections.push({ title: "People", items: peopleItems });
    }

    // Admin section
    if (isAdmin && !isImpersonating) {
      sections.push({
        title: "Admin",
        items: [
          { name: "Academic Years", href: routes.adminAcademicYears(), icon: CalendarDaysIcon },
          { name: "Departments", href: routes.adminDepartments(), icon: BuildingOffice2Icon },
          { name: "Approvals", href: routes.adminApprovals(), icon: CheckBadgeIcon },
          { name: "Grade Changes", href: routes.adminGradeChanges(), icon: PencilSquareIcon },
          { name: "Grade Posting", href: routes.adminGradePosting(), icon: ArrowUpTrayIcon },
          { name: "Audit Log", href: routes.adminAuditLog(), icon: ClipboardDocumentListIcon },
          { name: "Settings", href: routes.settings(), icon: Cog6ToothIcon },
        ],
      });
    }

    return sections;
  };

  const navSections = getNavSections();

  const SidebarContent = ({ onLinkClick }: { onLinkClick?: () => void }) => (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b border-neutral-200 px-4">
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-md shadow-primary-500/30">
          <svg
            className="h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
          GradeBook
        </h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        {navSections.map((section, sectionIdx) => (
          <div key={sectionIdx} className={sectionIdx > 0 ? "mt-6" : ""}>
            {section.title && (
              <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onLinkClick}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive(item.href)
                        ? "bg-primary-50 text-primary-700"
                        : "text-neutral-700 hover:bg-neutral-100"
                    )}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom section: Impersonation + User */}
      <div className="border-t border-neutral-200 p-4">
        {/* Impersonation dropdown - only for admins when not already impersonating */}
        {isAdmin && !isImpersonating && (
          <Menu as="div" className="relative mb-3">
            <Menu.Button
              disabled={impersonating}
              className="flex w-full items-center justify-between rounded-lg bg-neutral-100 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50"
            >
              <span className="flex items-center gap-2">
                <EyeIcon className="h-5 w-5" />
                View as
              </span>
              <ChevronDownIcon className="h-4 w-4" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute bottom-full left-0 right-0 z-10 mb-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="px-3 py-2 text-xs font-medium uppercase text-neutral-500">
                  Impersonate Role
                </div>
                {impersonationRoles.map(({ value, label }) => (
                  <Menu.Item key={value}>
                    {({ active }) => (
                      <Button
                        variant="ghost"
                        onClick={() => handleRoleSelect(value)}
                        className={cn(
                          active ? "bg-neutral-100" : "",
                          "h-auto w-full justify-start rounded-none px-4 py-2 text-left text-sm font-normal text-neutral-700"
                        )}
                      >
                        {label}
                      </Button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        )}

        {/* User info */}
        <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
          <UserCircleIcon className="h-10 w-10 text-neutral-400" />
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-neutral-900">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="truncate text-xs text-neutral-500">{user?.email}</p>
            <p className="text-xs text-neutral-400 capitalize">
              {user?.role}
              {isImpersonating && (
                <span className="ml-1 text-warning-600">(as {effectiveRole})</span>
              )}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="rounded-lg text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
            title="Sign out"
          >
            <ArrowRightOnRectangleIcon className="size-5" />
          </Button>
        </div>

        {/* Which bundle is running: commit sha + age of the deploy */}
        <BuildInfo />
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Button
        type="button"
        variant="ghost"
        size="icon-lg"
        className="fixed left-4 top-4 z-40 rounded-lg bg-white text-neutral-500 shadow-md hover:bg-neutral-100 hover:text-neutral-700 lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="size-6" />
      </Button>

      {/* Mobile sidebar overlay */}
      <Transition show={sidebarOpen} as={Fragment}>
        <div className="fixed inset-0 z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-neutral-900/50"
              onClick={() => setSidebarOpen(false)}
            />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="absolute right-4 top-4 rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="size-6" />
              </Button>
              <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
            </div>
          </Transition.Child>
        </div>
      </Transition>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto border-r border-neutral-200 bg-white">
          <SidebarContent />
        </div>
      </div>

      {/* User picker modal for impersonation */}
      {selectedRole && (
        <ImpersonationUserPicker
          isOpen={userPickerOpen}
          onClose={handleUserPickerClose}
          role={selectedRole}
          onSelectUser={handleUserSelect}
        />
      )}
    </>
  );
}
